/**
 * THE ONE ALIAS TABLE for the schedule code: the coverage check (scheduleCoverage.ts)
 * and the wording matcher (visitFormWording.ts) both read it, so "Pregnancy
 * Assessment", "CBC" and "Complete Blood Count" mean the same thing to the check
 * that asks "is there a form" and to the matcher that asks "which form".
 * Split out of scheduleCoverage.ts so the matcher can import it without a cycle.
 * Pure.
 */
import type { CoverageCategory } from "./scheduleCoverage";

/** British and American spellings meet: "haem" folds to "hem". */
export function fold(s: string): string {
  return s.replace(/haem/gi, "hem");
}

export interface AliasEntry {
  /** Canonical form name this entry stands for. */
  id: string;
  category: CoverageCategory;
  /** Normalised phrases (lower case, single spaces). A phrase matches on whole words. */
  phrases: readonly string[];
}

/**
 * THE ONE ALIAS TABLE. The classifier reads a schedule label through it and
 * isUnitCovered reads a form name through it, so "CBC", "Full Blood Count" and
 * "Hematology" are the same thing on both sides. Order matters: an entry's
 * matched phrase is removed before later entries are tried ("serious adverse
 * events" is consumed before "adverse events", so an SAE form never covers AE
 * collection). Pinned by a test.
 */
export const COVERAGE_ALIASES: readonly AliasEntry[] = Object.freeze([
  { id: "Serious Adverse Events", category: "safety", phrases: ["serious adverse events", "serious adverse event", "saes", "sae"] },
  { id: "Adverse Events", category: "safety", phrases: ["adverse events", "adverse event", "aes", "ae"] },
  { id: "Pregnancy Test", category: "safety", phrases: ["pregnancy test", "pregnancy testing", "pregnancy assessment", "pregnancy screen", "pregnancy screening"] },
  { id: "Haematology", category: "safety", phrases: ["haematology", "hematology", "cbc", "fbc", "complete blood count", "full blood count", "blood count"] },
  { id: "Clinical Chemistry", category: "safety", phrases: ["clinical chemistry", "serum chemistry", "blood chemistry", "biochemistry", "chemistry panel", "chemistry", "metabolic panel", "cmp", "bmp", "liver function", "renal function", "hba1c", "glycated haemoglobin", "glycated hemoglobin"] },
  { id: "Urinalysis", category: "safety", phrases: ["urinalysis", "urine analysis", "urine test", "urine dipstick", "dipstick"] },
  { id: "Coagulation", category: "safety", phrases: ["coagulation", "coag", "pt inr"] },
  { id: "Lipid Panel", category: "safety", phrases: ["lipid panel", "lipid profile", "lipids", "lipid"] },
  { id: "Serology", category: "safety", phrases: ["serology", "serologies"] },
  { id: "PK Sample Collection", category: "procedures", phrases: ["pharmacokinetic", "pharmacokinetics", "pk"] },
  { id: "Vital Signs", category: "safety", phrases: ["vital signs", "vital sign"] },
  { id: "Physical Examination", category: "safety", phrases: ["physical examination", "physical exam"] },
  { id: "ECG", category: "procedures", phrases: ["ecg", "ekg", "electrocardiogram"] },
  { id: "Spirometry", category: "procedures", phrases: ["spirometry"] },
  { id: "Informed Consent", category: "screening", phrases: ["informed consent"] },
  { id: "Eligibility Criteria", category: "screening", phrases: ["eligibility", "inclusion exclusion", "inclusion", "exclusion"] },
  { id: "Demographics", category: "demographics", phrases: ["demographics", "demographic"] },
  { id: "Medical History", category: "medical_history", phrases: ["medical history"] },
  { id: "Concomitant Medications", category: "safety", phrases: ["concomitant medications", "concomitant medication", "concomitant meds", "con meds", "conmeds", "con med"] },
  { id: "Randomisation", category: "administrative", phrases: ["enrollment and randomisation", "enrollment and randomization", "enrolment and randomisation", "enrolment and randomization", "randomisation", "randomization", "randomisation irt", "irt randomisation", "irt randomization"] },
  { id: "Study Completion", category: "administrative", phrases: ["disposition", "end of study", "end of treatment", "study completion"] },
] as AliasEntry[]);

function aliasText(s: string): string {
  return " " + fold(s).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim() + " ";
}

/** Every canonical id a piece of text stands for, in table order. */
export function canonicalIds(text: string): string[] {
  return canonicalParts(text).ids;
}

/** The canonical ids a text stands for, and the text left once their phrases are removed. */
export function canonicalParts(text: string): { ids: string[]; rest: string } {
  let t = aliasText(text);
  const out: string[] = [];
  for (const e of COVERAGE_ALIASES) {
    for (const ph of e.phrases) {
      const needle = " " + ph + " ";
      if (t.includes(needle)) {
        if (!out.includes(e.id)) out.push(e.id);
        t = t.split(needle).join(" ");
        break;
      }
    }
  }
  return { ids: out, rest: t.trim() };
}

